import { useCallback, useEffect, useState } from 'react';
import { ApiError, get, type Location } from './api';

/**
 * Change history (owner 10/26), admins only, at the bottom of Managers & access: every change people made to
 * schedules, the rotation, people, settings, emails and texts, newest first, with who did it.
 */
interface Item { id: string; when: string; store: string; by: string; what: string; category: string; categoryLabel: string }
interface Resp { items: Item[]; more: boolean; categories: { key: string; label: string }[] }

export function ChangeHistory({ locations }: { locations: Location[] }) {
  const [loc, setLoc] = useState<number | 'all'>('all');
  const [days, setDays] = useState(7);
  const [cat, setCat] = useState('');
  const [name, setName] = useState('');
  const [typed, setTyped] = useState('');
  const [d, setD] = useState<Resp | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => { const t = setTimeout(() => setName(typed.trim()), 400); return () => clearTimeout(t); }, [typed]);
  const load = useCallback(async () => {
    const p = new URLSearchParams({ days: String(days) });
    if (loc !== 'all') p.set('location', String(loc));
    if (cat) p.set('category', cat);
    if (name) p.set('name', name);
    try { setD(await get<Resp>(`/api/admin/history?${p}`)); setErr(''); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Can’t load the change history.'); }
  }, [days, loc, cat, name]);
  useEffect(() => { void load(); }, [load]);
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}><h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Change history</h2></div>
      <div style={{ fontSize: 13, color: '#4A5561' }}>Every change people made, newest first: schedules and posting, mid-day changes, the rotation, employees and their availability, time off, settings, emails and texts. “Name” finds both who made a change and who it was about. Only admins see this.</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 14px', fontSize: 14 }}>
        <label className="acclbl">Store
          <select className="sel-sm" value={loc} onChange={(e) => setLoc(e.target.value === 'all' ? 'all' : Number(e.target.value))} aria-label="Which store's changes">
            <option value="all">All stores</option>
            {locations.filter((l) => !l.demo).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </label>
        <label className="acclbl">Last
          <select className="sel-sm" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="How far back">
            <option value={1}>24 hours</option><option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option><option value={365}>Year</option>
          </select>
        </label>
        <label className="acclbl">Kind
          <select className="sel-sm" value={cat} onChange={(e) => setCat(e.target.value)} aria-label="Kind of change">
            <option value="">Everything</option>
            {(d?.categories ?? []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </label>
        <input className="inp" style={{ maxWidth: 220 }} placeholder="Name" value={typed} onChange={(e) => setTyped(e.target.value)} aria-label="Find a name" />
      </div>
      {err && <div className="errbar">{err}</div>}
      {d && !d.items.length && <div className="stnone">No changes in this time{name ? ' for that name' : ''}.</div>}
      {d && d.items.length > 0 && (
        <div style={{ border: '1px solid #C9CEC6', borderRadius: 8, background: '#FFFFFF', maxHeight: 560, overflowY: 'auto' }}>
          <table className="tbl" style={{ width: '100%' }}>
            <thead><tr><th style={{ whiteSpace: 'nowrap' }}>When</th><th>Store</th><th>Who</th><th>What changed</th><th>Kind</th></tr></thead>
            <tbody>{d.items.map((i) => (
              <tr key={i.id}>
                <td style={{ whiteSpace: 'nowrap', color: '#4A5561' }}>{i.when}</td>
                <td style={{ color: '#4A5561' }}>{i.store}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{i.by}</td>
                <td>{i.what}</td>
                <td style={{ color: '#4A5561', fontSize: 12.5, whiteSpace: 'nowrap' }}>{i.categoryLabel}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {d?.more && <div style={{ fontSize: 12.5, color: '#4A5561' }}>Showing the newest 500. Pick a store, a kind or a shorter time to see the rest.</div>}
    </>
  );
}
